import { Button, Icon } from '@shopify/polaris';
import {
  ThemeIcon,
  StoreIcon,
  ExitIcon,
  PersonIcon,
  UndoIcon,
  RedoIcon,
  HomeIcon,
  NotificationIcon,
  OrderIcon,
} from '@shopify/polaris-icons';
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/AuthProvider';
import { useEditorStore } from '@/state/editorStore';
import { usePublish, isPendingApproval, type PublishResult } from '@/queries/publish';
import { useSaveStaged, useDiscardStaged } from '@/queries/stagedDraft';
import { PageSelector } from './PageSelector';
import { ApiError } from '@/types/api';

// Custom top bar mirroring Shopify Theme Editor:
//   left:   exit + user menu
//   center: theme badge · store scope · PageSelector (drill-down)
//   right:  save status + Publish
export function TopBar() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const dirty = useEditorStore((s) => s.dirty);
  const saveStatus = useEditorStore((s) => s.saveStatus);
  const lastSavedAt = useEditorStore((s) => s.lastSavedAt);
  const undo = useEditorStore((s) => s.undo);
  const redo = useEditorStore((s) => s.redo);
  const canUndo = useEditorStore((s) => s.past.length > 0);
  const canRedo = useEditorStore((s) => s.future.length > 0);
  const publishMutation = usePublish();
  const saveMutation = useSaveStaged();
  const discardStaged = useDiscardStaged();
  const [userOpen, setUserOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setUserOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  // Cmd/Ctrl+Z undo · Cmd/Ctrl+Shift+Z (or Ctrl+Y) redo. Skipped while a
  // text field is focused so Polaris/native input undo keeps working.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target;
      if (t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement) return;
      if (!(e.metaKey || e.ctrlKey)) return;
      const key = e.key.toLowerCase();
      if (key === 'z') {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
      } else if (key === 'y') {
        e.preventDefault();
        redo();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [undo, redo]);

  const statusLabel = dirty
    ? 'Unsaved changes'
    : saveStatus === 'saving'
      ? 'Saving…'
      : saveStatus === 'error'
        ? 'Save failed'
        : lastSavedAt
          ? `Saved ${new Date(lastSavedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
          : 'No changes';

  const onSave = () => {
    saveMutation.mutate(undefined, {
      onError: (err: unknown) => {
        setToast(err instanceof ApiError ? err.message : 'Could not save');
        window.setTimeout(() => setToast(null), 4000);
      },
    });
  };

  const onDiscard = () => {
    if (window.confirm('Discard all unsaved changes?')) discardStaged();
  };

  const onPublish = () => {
    publishMutation.mutate(undefined, {
      onSuccess: (res: PublishResult) => {
        if (isPendingApproval(res)) {
          setToast('Sent to the main account for approval');
          window.setTimeout(() => setToast(null), 4000);
        } else {
          setToast('Theme published');
          window.setTimeout(() => setToast(null), 3000);
        }
      },
      onError: (err: unknown) => {
        setToast(err instanceof ApiError ? err.message : 'Could not publish');
        window.setTimeout(() => setToast(null), 4000);
      },
    });
  };

  const menuItemStyle: React.CSSProperties = {
    width: '100%',
    textAlign: 'left',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: '8px',
    borderRadius: 6,
    fontSize: 13,
  };

  const initials = (admin?.display_name || admin?.email || '?')
    .split(/\s+|@/)
    .map((s) => s[0]?.toUpperCase() ?? '')
    .slice(0, 2)
    .join('');

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        height: 56,
        padding: '0 12px',
        background: 'var(--p-color-bg-surface)',
        borderBottom: '1px solid var(--p-color-border)',
        gap: 12,
      }}
    >
      {/* Left */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 200 }}>
        <Button
          icon={ExitIcon}
          variant="tertiary"
          accessibilityLabel="Exit"
          onClick={() => {
            window.location.href = '/';
          }}
        />
        <Button
          icon={HomeIcon}
          variant="tertiary"
          accessibilityLabel="Dashboard"
          onClick={() => {
            window.location.href = '/dashboard';
          }}
        />
        <Button
          icon={NotificationIcon}
          variant="tertiary"
          accessibilityLabel="Push notifications"
          onClick={() => navigate('/push-notifications')}
        />
        <Button
          icon={OrderIcon}
          variant="tertiary"
          accessibilityLabel="Order confirmations"
          onClick={() => navigate('/comms/orders')}
        />
        <div ref={menuRef} style={{ position: 'relative' }}>
          <button
            type="button"
            onClick={() => setUserOpen((v) => !v)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px 6px',
              borderRadius: 8,
            }}
          >
            <span
              style={{
                width: 26,
                height: 26,
                borderRadius: '50%',
                background: 'var(--p-color-bg-fill-brand)',
                color: 'var(--p-color-text-brand-on-bg-fill)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
                fontWeight: 700,
              }}
            >
              {initials || <Icon source={PersonIcon} />}
            </span>
          </button>
          {userOpen && (
            <div
              style={{
                position: 'absolute',
                top: 38,
                left: 0,
                background: 'var(--p-color-bg-surface)',
                border: '1px solid var(--p-color-border)',
                borderRadius: 10,
                boxShadow: '0 8px 24px rgba(0,0,0,0.14)',
                minWidth: 200,
                zIndex: 50,
                padding: 8,
              }}
            >
              <div style={{ padding: '6px 8px', fontSize: 12, color: 'var(--p-color-text-subdued)' }}>
                {admin?.display_name || admin?.email}
              </div>
              {admin?.role === 'admin' ? (
                <button
                  type="button"
                  onClick={() => {
                    setUserOpen(false);
                    navigate('/users');
                  }}
                  style={menuItemStyle}
                >
                  Manage users
                </button>
              ) : null}
              {admin?.is_main ? (
                <button
                  type="button"
                  onClick={() => {
                    setUserOpen(false);
                    navigate('/approvals');
                  }}
                  style={menuItemStyle}
                >
                  Publish approvals
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => {
                  setUserOpen(false);
                  void logout();
                }}
                style={menuItemStyle}
              >
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Center — theme · store · page selector */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 18,
        }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <span style={{ width: 16, height: 16, display: 'inline-flex' }}>
            <Icon source={ThemeIcon} />
          </span>
          <span style={{ fontWeight: 600 }}>Padel Needs</span>
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--p-color-text-success)',
              background: 'var(--p-color-bg-fill-success-secondary)',
              borderRadius: 999,
              padding: '2px 8px',
            }}
          >
            Live
          </span>
        </span>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 13,
            color: 'var(--p-color-text-subdued)',
          }}
        >
          <span style={{ width: 16, height: 16, display: 'inline-flex' }}>
            <Icon source={StoreIcon} />
          </span>
          Store default
        </span>
        <PageSelector />
      </div>

      {/* Right — status + publish */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          minWidth: 200,
          justifyContent: 'flex-end',
        }}
      >
        <Button
          icon={UndoIcon}
          variant="tertiary"
          accessibilityLabel="Undo (⌘Z)"
          disabled={!canUndo}
          onClick={undo}
        />
        <Button
          icon={RedoIcon}
          variant="tertiary"
          accessibilityLabel="Redo (⇧⌘Z)"
          disabled={!canRedo}
          onClick={redo}
        />
        <span style={{ fontSize: 12, color: 'var(--p-color-text-subdued)' }}>
          {toast ?? statusLabel}
        </span>
        {dirty && (
          <Button onClick={onDiscard} disabled={saveMutation.isPending}>
            Discard
          </Button>
        )}
        <Button
          variant="primary"
          onClick={onSave}
          loading={saveMutation.isPending}
          disabled={!dirty}
        >
          Save
        </Button>
        <Button onClick={onPublish} loading={publishMutation.isPending}>
          Publish
        </Button>
      </div>
    </div>
  );
}
